/* =========================================================
   4PIXELS — CART RUNTIME V12
   Single runtime owner for cart re-hydration, close glyph and preserving the
   header's pre-cart visual state. No injected duplicate CSS.
   ========================================================= */

(() => {
  'use strict';

  if (window.__fpCartRuntimeV12) return;

  const q = (selector, root = document) => root.querySelector(selector);
  let raf = 0;
  let miniCartObserver = null;
  let observedMiniCart = null;
  let detailsObserver = null;
  let observedDetails = null;

  function updateCartHeaderBubble(sectionHtml, fallbackCount) {
    const target = document.getElementById('cart-icon-bubble');
    if (!target) return;

    let incoming = null;
    if (sectionHtml) {
      try {
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        incoming = doc.querySelector('.cart-count-bubble');
      } catch (_error) {}
    }

    let bubble = target.querySelector('[data-cart-count], .cart-count-bubble');
    if (!bubble) {
      bubble = document.createElement('span');
      bubble.className = 'fp-header__count cart-count-bubble';
      bubble.setAttribute('data-cart-count', '');
      target.appendChild(bubble);
    }

    if (incoming) {
      bubble.classList.toggle('cart-count-bubble--dot', incoming.classList.contains('cart-count-bubble--dot'));
      bubble.innerHTML = incoming.innerHTML;
      const aria = incoming.getAttribute('aria-label');
      if (aria) bubble.setAttribute('aria-label', aria);
      bubble.hidden = false;
      bubble.removeAttribute('hidden');
      return;
    }

    const count = Math.max(0, Number(fallbackCount || 0));
    bubble.classList.toggle('cart-count-bubble--dot', count >= 100);
    bubble.innerHTML = count < 100 ? `<span aria-hidden="true">${count}</span>` : '';
    bubble.setAttribute('aria-label', `${count} cart items`);
    bubble.hidden = false;
    bubble.removeAttribute('hidden');
  }

  window.__fpUpdateCartHeaderBubble = window.__fpUpdateCartHeaderBubble || updateCartHeaderBubble;

  function important(element, property, value) {
    if (element) element.style.setProperty(property, value, 'important');
  }

  function clear(element, properties) {
    if (!element) return;
    properties.forEach((property) => element.style.removeProperty(property));
  }

  function ensureCloseGlyph() {
    const miniCart = document.getElementById('mini-cart');
    if (!miniCart) return;

    const button = q('.mini-cart__header > button.header__icon--cart', miniCart);
    if (!button) return;

    button.setAttribute('aria-label', document.documentElement.dir === 'rtl' ? 'إغلاق السلة' : 'Close cart');

    button.querySelectorAll(':scope > svg:not(.fp-cart-close-glyph)').forEach((svg) => svg.remove());

    let glyph = q(':scope > svg.fp-cart-close-glyph', button);
    if (!glyph) {
      glyph = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      glyph.classList.add('fp-cart-close-glyph');
      glyph.setAttribute('viewBox', '0 0 24 24');
      glyph.setAttribute('aria-hidden', 'true');
      glyph.setAttribute('focusable', 'false');

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M6.5 6.5 17.5 17.5M17.5 6.5 6.5 17.5');
      glyph.appendChild(path);
      button.appendChild(glyph);
    }
  }

  const shellProps = [
    '--fp-current-bg','--fp-current-text','--fp-current-icon',
    'background','background-color','background-image','color',
    'border','border-bottom','box-shadow'
  ];

  function clearHeaderLock() {
    const shell = q('[data-fp-header-shell]');
    if (!shell) return;

    clear(shell, shellProps);
    clear(q(':scope > .fp-header', shell), [
      'background','background-color','background-image','color','border','border-bottom','box-shadow'
    ]);

    shell.querySelectorAll('.fp-header__menu-toggle,.fp-header__actions .fp-header__icon,.fp-header__brand-link,.fp-header__brand-text,.fp-header__desktop-nav .header__menu-item')
      .forEach((element) => clear(element, ['color','-webkit-text-fill-color']));

    shell.querySelectorAll('.fp-header__logo').forEach((logo) => clear(logo, ['display']));
    shell.querySelectorAll('.fp-header__logo img').forEach((image) => clear(image, ['background','filter']));
  }

  function lockHeaderWhileCartOpen() {
    const shell = q('[data-fp-header-shell]');
    const details = shell && q('[data-fp-cart-details]', shell);

    if (!shell || !details || !details.open) {
      clearHeaderLock();
      return;
    }

    clearHeaderLock();

    const header = q(':scope > .fp-header', shell);
    const computed = getComputedStyle(shell);
    const transparent = shell.classList.contains('is-transparent-capable') && !shell.classList.contains('is-scrolled');

    if (transparent) {
      const text = computed.getPropertyValue('--fp-transparent-text').trim() || '#FFFFFF';

      important(shell, '--fp-current-bg', 'transparent');
      important(shell, '--fp-current-text', text);
      important(shell, '--fp-current-icon', '#FFFFFF');
      important(shell, 'background', 'transparent');
      important(shell, 'background-color', 'transparent');
      important(shell, 'background-image', 'none');
      important(shell, 'color', text);
      important(shell, 'border', '0');
      important(shell, 'border-bottom', '0');
      important(shell, 'box-shadow', 'none');

      important(header, 'background', 'transparent');
      important(header, 'background-color', 'transparent');
      important(header, 'background-image', 'none');
      important(header, 'color', text);
      important(header, 'border', '0');
      important(header, 'border-bottom', '0');
      important(header, 'box-shadow', 'none');

      shell.querySelectorAll('.fp-header__menu-toggle,.fp-header__actions .fp-header__icon,.fp-header__brand-link,.fp-header__brand-text,.fp-header__desktop-nav .header__menu-item')
        .forEach((element) => {
          important(element, 'color', '#FFFFFF');
          important(element, '-webkit-text-fill-color', '#FFFFFF');
        });

      shell.querySelectorAll('.fp-header__logo').forEach((logo) => important(logo, 'display', 'none'));
      const normalLogo = q('.fp-header__logo--normal', shell);
      if (normalLogo) {
        important(normalLogo, 'display', 'block');
        const image = q('img', normalLogo);
        if (image) {
          important(image, 'background', 'transparent');
          important(image, 'filter', 'brightness(0) invert(1)');
        }
      }
      return;
    }

    const bg = computed.getPropertyValue('--fp-solid-bg').trim() || '#FFFFFF';
    const text = computed.getPropertyValue('--fp-solid-text').trim() || '#000000';
    const icon = computed.getPropertyValue('--fp-solid-icon').trim() || '#000000';

    important(shell, '--fp-current-bg', bg);
    important(shell, '--fp-current-text', text);
    important(shell, '--fp-current-icon', icon);
    important(shell, 'background', bg);
    important(shell, 'background-color', bg);
    important(shell, 'background-image', 'none');
    important(shell, 'color', text);

    important(header, 'background', 'transparent');
    important(header, 'background-color', 'transparent');
    important(header, 'background-image', 'none');
    important(header, 'color', text);

    shell.querySelectorAll('.fp-header__menu-toggle,.fp-header__actions .fp-header__icon')
      .forEach((element) => {
        important(element, 'color', icon);
        important(element, '-webkit-text-fill-color', icon);
      });
  }

  function observeCurrentNodes() {
    const shell = q('[data-fp-header-shell]');
    const details = shell && q('[data-fp-cart-details]', shell);
    const miniCart = document.getElementById('mini-cart');

    if (details && details !== observedDetails && 'MutationObserver' in window) {
      if (detailsObserver) detailsObserver.disconnect();
      observedDetails = details;
      detailsObserver = new MutationObserver(schedule);
      detailsObserver.observe(details, { attributes: true, attributeFilter: ['open','class'] });
    }

    if (miniCart && miniCart !== observedMiniCart && 'MutationObserver' in window) {
      if (miniCartObserver) miniCartObserver.disconnect();
      observedMiniCart = miniCart;
      miniCartObserver = new MutationObserver(schedule);
      miniCartObserver.observe(miniCart, { childList: true, subtree: true });
    }
  }

  function run() {
    ensureCloseGlyph();
    lockHeaderWhileCartOpen();
    observeCurrentNodes();
  }

  function schedule() {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      run();
    });
  }

  document.addEventListener('cartdrawer:hydrated', schedule);
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('cart:refresh', schedule);
  document.addEventListener('ajaxProduct:added', schedule);
  document.addEventListener('shopify:section:load', schedule);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('scroll', schedule, { passive: true });

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-fp-cart-details] summary,.mini-cart__header > button.header__icon--cart')) {
      setTimeout(schedule, 0);
      setTimeout(schedule, 180);
    }
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
  else schedule();

  window.__fpCartRuntimeV12 = { run, schedule, ensureCloseGlyph, lockHeaderWhileCartOpen };
})();

class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.drawer = document.querySelector('cart-drawer');
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this));
    this.observer.observe(this);
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        const target = document.getElementById('mini-cart');
        if (!target) return;
        target.innerHTML = this.getSectionInnerHTML(html, '.shopify-section');
        document.dispatchEvent(new CustomEvent('cartdrawer:hydrated'));
        if (window.__fpCartRuntimeV12) window.__fpCartRuntimeV12.schedule();
      })
      .catch((error) => console.error(error));
  }

  open() {
    const details = this.drawer && this.drawer.querySelector('details');
    if (!details || details.hasAttribute('open')) return;
    const summary = details.querySelector('summary');
    if (summary) summary.click();
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = parsedState.sections && parsedState.sections[section.section];
      if (!sectionHtml) return;

      if (section.id === 'cart-icon-bubble') {
        if (window.__fpUpdateCartHeaderBubble) window.__fpUpdateCartHeaderBubble(sectionHtml);
        return;
      }

      const element = document.getElementById(section.id);
      if (element) element.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    if (window.__fpCartRuntimeV12) window.__fpCartRuntimeV12.schedule();
  }

  getSectionsToRender() {
    return [
      { id: 'mini-cart', section: 'mini-cart', selector: '.shopify-section' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }
}

if (!customElements.get('mini-cart')) customElements.define('mini-cart', MiniCart);
